import React from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useTimeModal } from '../TimeModal/TimeModalContext';

import { formatNull, formatDate, isDatePastInMoscow, formatCaseStatus, formatOverflow } from '../../Utils/GlobalUtils';

import LoadingLines from '../LoadingLines';

import styles from '../../styles/DynamicModal.module.css';
import stylesSearch from '../../styles/Search.module.css';

import IconContainer from '../../Components/IconContainer';
import { ReactComponent as XIcon } from '../../icons/x-close.svg';
import AddNewDocumentToApplication from '../AddNewDocumentToApplication';

const SearchAMProposedCases = ({ closeModal, userId }) => {
    const { openTimeModal } = useTimeModal();
    const navigate = useNavigate();
    const location = useLocation();

    const [cases, setCases] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [warning, setWarning] = React.useState('');

    const inputRef = React.useRef(null);

    React.useEffect(() => {
        inputRef.current?.focus();
    }, []); 

    const handleLogout = () => {
        localStorage.removeItem('token');
        window.location.href = '/';
    }

    const [searchTerm, setSearchTerm] = React.useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500); // задержка 500 мс

        return () => {
            clearTimeout(handler);
        };
    }, [searchTerm]);

    React.useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) handleLogout();
        if (debouncedSearchTerm.trim() == '') {
            setWarning('Введите поисковый запрос');
        } else {
            setLoading(true);

            axios.get(`/api/search-am-proposed-cases?searchterm=${debouncedSearchTerm}&user_id=${userId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            })
            .then((response) => {
                if (response.data.length === 0) {
                    setCases([]);
                    setWarning('Ничего не найдено');
                } else {
                    setCases(response.data);
                    setWarning('');
                }
            })
            .catch((error) => {
                console.error(error);
                setWarning(`${error.response?.data.message || 'Произошла неизвестная ошибка. Попробуйте повторить позднее'}`);
            })
            .finally(() => {
                setLoading(false);
            })
        }
    }, [debouncedSearchTerm]);

    const [isNewDocumentToApplication, setIsNewDocumentToApplication] = React.useState(false);
    const [selectedApplicationId, setSelectedApplicationId] = React.useState('');
    const [selectedCaseId, setSelectedCaseId] = React.useState('');


    return (
    <>
        {isNewDocumentToApplication && 
        <AddNewDocumentToApplication
            applicationId={selectedApplicationId} 
            caseId={selectedCaseId} 
            isFirst={true} 
            closeModal={() => {setIsNewDocumentToApplication(false)}} 
            onSuccess={() => setCases(prevCases =>
                prevCases.map(item =>
                    item.id === selectedApplicationId ? { ...item, isSent: true } : item
                )
            )}
        />}

        <div className={styles.liningOver}>
            <motion.div
                className={styles.lining} 
                onClick={(e) => e.stopPropagation()}
                initial={{ scale: '97%', opacity: 0 }}
                animate={{ scale: '100%', opacity: 1 }}
                exit={{ scale: '97%', opacity: 0 }}
                transition={{ duration: 0.3 }}
            >
                <div className={styles.header}>
                    <h1>Поиск</h1>
                    <IconContainer Icon={XIcon} onClick={closeModal} size = '20' color = 'black' className = '' />
                </div>
                <div className={styles.main} style={{height: '80vh'}}>
                    <div className={stylesSearch.searchInputWrapper}>
                        <input type='text' value={searchTerm} onChange={(e) => {setSearchTerm(e.target.value)}} placeholder='Номер дела, должник...' ref={inputRef} />
                    </div>
                    <div className={stylesSearch.mainWrapper}>
                        {loading ? <LoadingLines />
                        : warning ? <a className={`description ${stylesSearch.centerWarning}`}>{warning}</a>
                        : <>
                            <a className='description'>Первые 10 совпадений</a>
                            <div
                                className='tableRow nonActive' 
                                style={{gridTemplateColumns: '16fr 25fr 13fr 13fr 16fr 15fr'}} 
                            >
                                <div>Номер</div>
                                <div>Процедура / Должник</div>
                                <div>Дата рассылки предложения</div>
                                <div>Крайний срок предоставления согласия</div>
                                <div>Количество претендующих</div>
                                <div></div>
                            </div>
                            {cases.map(caseItem => (
                                caseItem.isSent ? (
                                    <div
                                        className='tableRow nonActive successRow'
                                        key={caseItem.id}
                                        style={{ gridTemplateColumns: '1fr' }}
                                    >
                                        <div
                                            style={{height: '40px', margin: '0 15px'}}
                                        >
                                            <p
                                                style={{display: 'flex', alignItems: 'center', gap: '15px'}}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check-circle-fill" viewBox="0 0 16 16">
                                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                                                </svg>
                                                Согласие отправлено. Отслеживать статус заявки можно во вкладке «Мои заявки»
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                <motion.div
                                    className='tableRow nonActive'
                                    style={{gridTemplateColumns: '16fr 25fr 13fr 13fr 16fr 15fr'}}
                                    key={caseItem.id}
                                    initial={{ scale: '97%', opacity: 0 }}
                                    animate={{ scale: '100%', opacity: 1 }}
                                    exit={{ scale: '97%', opacity: 0 }}
                                    transition={{ duration: 0.3 }}
                                    layout
                                >
                                    <div>
                                        {formatNull(caseItem.number)}
                                        <a className='description'>{formatNull(caseItem.internal_number)}</a>
                                        {cases.to_case_number && 'Жалоба на: '+caseItem.to_case_number}
                                    </div>
                                    <div>
                                        <span className='CellForHorizontalLine'>
                                            <p
                                                className='grayText'
                                            >
                                                {formatNull(caseItem.procedure_name)}
                                            </p>
                                            <span className='horizontalLine'></span>
                                            {caseItem.debtors ?
                                                <p>
                                                    {formatOverflow(caseItem.debtors.map((e) => e.name).join(', '))}
                                                </p>
                                                :
                                                formatNull(caseItem.debtors)
                                            }
                                        </span>
                                    </div>
                                    <div>{caseItem.mailing_status==='Письма не отправлены' ? <p>Письма не отправлены</p> : formatDate(caseItem.date_mailing)}</div>
                                    <div>
                                        <p>{formatDate(caseItem.date_consent_deadline)}</p>
                                        <p>{!caseItem.is_consent_deadline && '[СРО временно продлила срок приёма согласий]'}</p>
                                    </div>
                                    <div>
                                        {caseItem.status=='Управляющий не назначен' ? 
                                        <p className='description'>{caseItem.status}</p>
                                        :
                                        caseItem.user ? 
                                        <p className='description'>Кандидатура управляющего выбрана СРО</p>
                                        :
                                        <p>{caseItem.count_pretenders}</p>}
                                    </div>
                                    <div>
                                        {(caseItem.user_id!=='' && caseItem.user_id!==null) &&
                                        <button 
                                            type='button' 
                                            className='miniButton' 
                                            onClick={() => {
                                                if (!isDatePastInMoscow(caseItem.date_consent_deadline) || !caseItem.is_consent_deadline) {
                                                    setIsNewDocumentToApplication(true); setSelectedApplicationId(caseItem.id); setSelectedCaseId(caseItem.case_id);
                                                } else {
                                                    openTimeModal('Крайний срок предоставления согласия прошел', 'warning');
                                                }
                                            }}
                                            style={(!isDatePastInMoscow(caseItem.date_consent_deadline) || !caseItem.is_consent_deadline) ? {} : {opacity: '.3'}}
                                        >
                                            Отправить согласие
                                        </button>}
                                    </div>
                                </motion.div>)
                            ))}
                        </>
                        }
                    </div>
                </div>
            </motion.div>
        </div>
    </>
    );
};

export default SearchAMProposedCases;
